import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { useAppDispatch } from '../store/hooks'
import { deleteApp } from '../store/appsSlice'
import './DeleteAppModal.css'

interface DeleteAppModalProps {
  appId: string
  appName: string
  onClose: () => void
}

function DeleteAppModal({ appId, appName, onClose }: DeleteAppModalProps) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement
    modalRef.current?.focus()

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previousFocusRef.current?.focus()
    }
  }, [onClose])

  const handleDelete = async () => {
    setDeleting(true)
    setError(null)

    try {
      await dispatch(deleteApp(appId)).unwrap()
      navigate('/')
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to delete app')
      setDeleting(false)
    }
  }

  return (
    <div className="delete-modal-overlay" data-testid="delete-app-modal-overlay" onClick={onClose}>
      <div
        className="delete-modal"
        data-testid="delete-app-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-app-modal-title"
        ref={modalRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="delete-modal__header">
          <h2 className="delete-modal__title" id="delete-app-modal-title" data-testid="delete-app-modal-title">Delete App</h2>
          <button
            className="delete-modal__close"
            onClick={onClose}
            aria-label="Close dialog"
            data-testid="delete-app-modal-close"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <p className="delete-modal__description" data-testid="delete-app-modal-description">
          Are you sure you want to delete <strong>{appName}</strong>? This action will mark the app as deleted and it will no longer appear in your dashboard.
        </p>
        {error && (
          <p className="delete-modal__error" role="alert" data-testid="delete-app-error">
            {error}
          </p>
        )}
        <div className="delete-modal__actions">
          <button
            type="button"
            className="delete-modal__btn delete-modal__btn--cancel"
            onClick={onClose}
            disabled={deleting}
            data-testid="delete-app-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="delete-modal__btn delete-modal__btn--delete"
            onClick={handleDelete}
            disabled={deleting}
            data-testid="delete-app-confirm"
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DeleteAppModal
